import { Box, Container } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { CVPreviewSidebar } from "../../components/common/CVPreviewSidebar";
import { FoundCountBadge } from "../../components/common/FoundCountBadge";
import { SidePanelShell } from "../../components/common/SidePanelShell";
import { useTranslation } from "../../hooks/useTranslation";
import { BulkAvailabilityModal } from "./components/bulk-operations/BulkAvailabilityModal";
import { SelectionModeToolbar } from "./components/bulk-operations/SelectionModeToolbar";
import { EmployeeOnlyPanel } from "./components/EmployeeOnlyPanel";
import { EmployeeOverviewGrid } from "./components/EmployeeOverviewGrid";
import { EmployeeOverviewPagination } from "./components/EmployeeOverviewPagination";
import { EmployeeOverviewSearch } from "./components/EmployeeOverviewSearch";
import { EmployeeOverviewTable } from "./components/EmployeeOverviewTable";
import { EmployeeOverviewToolbar } from "./components/EmployeeOverviewToolbar";
import { useEmployeeOverviewActions } from "./hooks/useEmployeeOverviewActions";
import { useEmployeeOverviewData } from "./hooks/useEmployeeOverviewData";
import { useEmployeeOverviewFilters } from "./hooks/useEmployeeOverviewFilters";
import { useEmployeeSelection } from "./hooks/useEmployeeSelection";
import { EmployeeOverviewListItem } from "./models/employeeOverview.model";
import { employeeOverviewService } from "./services/employeeOverviewService";

export { employeeOverviewService } from "./services/employeeOverviewService";
export type { BulkSetEmployeeAvailabilityDateRequest, GetEmployeeOverviewPageRequest } from "./services/employeeOverviewService";

export { useEmployeeOverviewActions } from "./hooks/useEmployeeOverviewActions";
export { employeeOverviewQueryKey, useEmployeeOverviewData } from "./hooks/useEmployeeOverviewData";
export { useEmployeeOverviewFilters } from "./hooks/useEmployeeOverviewFilters";
export { useEmployeeFilterDictionaries } from "./hooks/useEmployeeFilterDictionaries";
export { useEmployeeSelection } from "./hooks/useEmployeeSelection";

export type {
    EmployeeAllocationSummary,
    EmployeeListFilter,
    EmployeeOverviewListItem,
    EmployeeOverviewPage,
} from "./models/employeeOverview.model";

type ViewMode = "grid" | "list";
const VIEW_MODE_STORAGE_KEY = "employee-overview-view-mode";

const getInitialViewMode = (): ViewMode =>
    localStorage.getItem(VIEW_MODE_STORAGE_KEY) === "list" ? "list" : "grid";

export function EmployeeOverview() {
    const { t } = useTranslation();
    const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);
    const isMobile = useMediaQuery("(max-width: 768px)");

    const { selectedEmployee, handleSelectEmployee, handleClosePreview } = useEmployeeOverviewActions();

    const selection = useEmployeeSelection();
    const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

    const filters = useEmployeeOverviewFilters();
    const { data, isLoading, refetch } = useEmployeeOverviewData(filters.filter);

    const employees: EmployeeOverviewListItem[] = data?.page ?? [];
    const totalItems = data?.allDataCount ?? 0;
    const totalPages = Math.ceil(totalItems / filters.pageSize);

    const handleViewModeChange = (value: string) => {
        const newViewMode = value as ViewMode;
        setViewMode(newViewMode);
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, newViewMode);
    };

    const handleConfirmAvailability = async (availabilityDate: string | undefined) => {
        try {
            await employeeOverviewService.bulkSetAvailabilityDate({
                employeeIds: Array.from(selection.selectedIds),
                availabilityDate,
            });
            notifications.show({
                title: t("EMPLOYEEOVERVIEW_NOTIF_AVAILABILITY_UPDATED_TITLE", undefined, "Availability updated"),
                message: availabilityDate
                    ? t(
                        "EMPLOYEEOVERVIEW_NOTIF_AVAILABILITY_SET_MESSAGE",
                        { count: selection.selectedIds.size.toString() },
                        `Set availability for ${selection.selectedIds.size} employee(s).`
                    )
                    : t(
                        "EMPLOYEEOVERVIEW_NOTIF_AVAILABILITY_CLEARED_MESSAGE",
                        { count: selection.selectedIds.size.toString() },
                        `Cleared availability for ${selection.selectedIds.size} employee(s).`
                    ),
                color: "green",
            });
            setAvailabilityModalOpen(false);
            selection.handleExitSelectionMode();
            await refetch();
        } catch (error) {
            console.error("Failed to bulk-update availability:", error);
            notifications.show({
                title: t("EMPLOYEEOVERVIEW_NOTIF_UPDATE_FAILED_TITLE", undefined, "Update failed"),
                message: t(
                    "EMPLOYEEOVERVIEW_NOTIF_UPDATE_FAILED_MESSAGE",
                    undefined,
                    "Could not update availability for the selected employees. Please try again."
                ),
                color: "red",
            });
        }
    };

    return (
        <Container size="xl" p={0} fluid>
            <Box
                p="md"
                style={{
                    marginRight: selectedEmployee && !isMobile ? "400px" : "0",
                    transition: "margin-right 0.3s ease",
                }}
            >
                <EmployeeOverviewSearch filters={filters} />

                <FoundCountBadge
                    count={totalItems}
                    singularNoun={t("EMPLOYEEOVERVIEW_FOUND_SINGULAR", undefined, "employee")}
                    pluralNoun={t("EMPLOYEEOVERVIEW_FOUND_PLURAL", undefined, "employees")}
                />

                <SelectionModeToolbar
                    selectionMode={selection.selectionMode}
                    onEnterSelectionMode={selection.handleEnterSelectionMode}
                    pageEmployees={employees}
                    selectedIds={selection.selectedIds}
                    onSelectAllOnPage={() => selection.handleSelectAllOnPage(employees)}
                    onDeselectAllOnPage={() => selection.handleDeselectAllOnPage(employees)}
                    onClearAll={selection.handleClearAll}
                    onCancel={selection.handleExitSelectionMode}
                    onSetAvailability={() => setAvailabilityModalOpen(true)}
                    hasResults={totalItems > 0}
                    loading={isLoading}
                />

                <EmployeeOverviewToolbar
                    pageSize={filters.pageSize}
                    onPageSizeChange={filters.handlePageSizeChange}
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                />

                {viewMode === "grid" ? (
                    <EmployeeOverviewGrid
                        employees={employees}
                        loading={isLoading}
                        isPanelOpen={!!selectedEmployee}
                        selectionMode={selection.selectionMode}
                        selectedIds={selection.selectedIds}
                        onToggleSelect={selection.handleToggleSelect}
                        onSelectEmployee={handleSelectEmployee}
                    />
                ) : (
                    <EmployeeOverviewTable
                        employees={employees}
                        loading={isLoading}
                        sortField={filters.sortField}
                        sortDirection={filters.sortDirection}
                        onSortChange={filters.handleSortChange}
                        selectionMode={selection.selectionMode}
                        selectedIds={selection.selectedIds}
                        onToggleSelect={selection.handleToggleSelect}
                        onSelectEmployee={handleSelectEmployee}
                    />
                )}

                <EmployeeOverviewPagination
                    totalPages={totalPages}
                    pageIndex={filters.pageIndex}
                    pageSize={filters.pageSize}
                    totalItems={totalItems}
                    onPageChange={filters.handlePageChange}
                />
            </Box>

            {selectedEmployee && (
                <SidePanelShell onClose={handleClosePreview}>
                    {selectedEmployee.personId ? (
                        <CVPreviewSidebar
                            isEditable={true}
                            personId={selectedEmployee.personId}
                            onClose={handleClosePreview}
                            onDownloadCV={() => { }}
                        />
                    ) : (
                        <EmployeeOnlyPanel employee={selectedEmployee} onClose={handleClosePreview} />
                    )}
                </SidePanelShell>
            )}

            <BulkAvailabilityModal
                opened={availabilityModalOpen}
                onClose={() => setAvailabilityModalOpen(false)}
                selectedCount={selection.selectedIds.size}
                onConfirm={handleConfirmAvailability}
            />
        </Container>
    );
}
